import { ProgressBar } from "@dropbox/dig-components/progress_indicators";
import { Title } from "@dropbox/dig-components/typography";
import { LookMagnifyingGlassSpot } from "@dropbox/dig-illustrations";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Select_DashUxa } from "@mirage/analytics/events/types/select_dash_uxa";
import { useFlyoutPanel } from "@mirage/mosaics/FlyoutPanel/useFlyoutPanel";
import { StacksLoadingContainer } from "@mirage/mosaics/StacksLoading/StacksLoadingContainer";
import { auditLogUserOpenedStack } from "@mirage/service-audit-logging";
import { getLoginPathWithReturnRedirectURLParam } from "@mirage/service-auth";
import { useAccountIsDropboxer } from "@mirage/service-auth/useDropboxAccount";
import { useIsLoggedIn } from "@mirage/service-auth/useDropboxAuthentication";
import { useInitStackComments } from "@mirage/service-comments/hooks/useInitStackComments";
import { callApiV2 } from "@mirage/service-dbx-api";
import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { tagged } from "@mirage/service-logging";
import { namespace } from "@mirage/service-operational-metrics";
import {
  MetricPageName,
  StackDetailsModule,
} from "@mirage/service-operational-metrics/module/constants";
import {
  useAutoRecordAggregatedPageLatency,
  useRecordModuleLatency,
} from "@mirage/service-operational-metrics/module/module";
import {
  getStacksDataCachedTags,
  useStacksDataCachedTags,
} from "@mirage/service-operational-metrics/module/tags";
import { logPageLoadMilestoneOnce } from "@mirage/service-operational-metrics/page-load";
import { previewStack } from "@mirage/service-stacks";
import { useDashTitle } from "@mirage/shared/hooks/DashTitle";
import { Backoff } from "@mirage/shared/util/backoff";
import { FullScreenPublicStackV2, FullScreenStackV2 } from "@mirage/stacks";
import { useInitCommentMetadata } from "@mirage/stacks/Comments/hooks/useInitCommentMetadata";
import { useEnrichedStackItems } from "@mirage/stacks/hooks";
import { useStackChatSources } from "@mirage/stacks/hooks/FullScreenStackHooks";
import { useSetActiveStack } from "@mirage/stacks/hooks/useSetActiveStack";
import { StackDetailPageV3 } from "@mirage/stacks/StackDetailPage/StackDetailPageV3";
import i18n from "@mirage/translations";
import type { MutableRefObject } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useNotifyInStackDetailPage } from "./hooks/useNotifyInStackDetailPage";
import { useStackOrRedirectToLogin } from "./hooks/useStackOrRedirectToLogin";
import { useStackUnsubscribeUrl } from "./hooks/useStackUnsubscribeUrl";
import styles from "./StackPage.module.css";

const logger = tagged("StackPage");
const metrics = namespace("stacks/stack_page");

const MAX_PREVIEW_ATTEMPTS = 20;

interface StackPageProps {
  shareId: string;
}

// A simple wrapper to be used by hornet
export const StackPageWithoutShareId = () => {
  const { shareId } = useParams();
  return <StackPage shareId={shareId ?? ""} />;
};

const enum ViewMode {
  Loading,
  Loaded,
  NotFound,
}

export const StackPage = ({ shareId }: StackPageProps) => {
  const [viewMode, setViewMode] = useState(ViewMode.Loading);
  const { stack, items, publicPreview, featureFlagsReady } =
    useStackOrRedirectToLogin(shareId);
  const { account: currentAccount, isDropboxer } = useAccountIsDropboxer();
  const [showSharingModule, setShowSharingModule] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const isLoggedIn = useIsLoggedIn();
  const navigate = useNavigate();
  const enrichedStackItems = useEnrichedStackItems(stack?.namespace_id, items);
  const isFssStacksEnabled = useFeatureFlagValueBool(
    "dash_2025_06_24_fss_stacks",
  );
  const notifyInStackDetailPage = useNotifyInStackDetailPage();
  const { hasChatSources } = useStackChatSources();
  const isNewStack = !stack?.namespace_id;
  const isChatEnabled = hasChatSources && !isNewStack;

  // Initialization for Stack comments
  useInitStackComments(viewMode === ViewMode.Loaded || !!stack, shareId);
  useInitCommentMetadata();

  // Initialize the active stack atoms
  useSetActiveStack(stack ?? null, enrichedStackItems ?? null);

  // Handle disabling notifications triggered by an unsubscribe link
  useStackUnsubscribeUrl(stack);

  // Detect the condition where the stack was rendered, then disappears.
  const lastRenderedShareId = useRef("");

  const tryRedirectToLoginPage = useCallback(
    async function () {
      if (isLoggedIn || !featureFlagsReady || publicPreview) {
        return false;
      }
      const loginPath = await getLoginPathWithReturnRedirectURLParam();
      navigate(loginPath);
      return true;
    },
    [navigate, isLoggedIn, publicPreview, featureFlagsReady],
  );

  const hasSentCanonicalRequest: MutableRefObject<string | null> = useRef(null);
  const tryRedirectToCanonicalShareUrl = useCallback(
    async function () {
      if (hasSentCanonicalRequest.current === shareId) {
        return false;
      }
      hasSentCanonicalRequest.current = shareId;
      try {
        const { canonical_sharing_id: canonicalShareId } = await callApiV2(
          "stacksGetCanonicalSharingId",
          {
            sharing_id: shareId,
          },
        );
        if (canonicalShareId && canonicalShareId !== shareId) {
          metrics.counter("redirected_to_canonical_share_id", 1, {
            teamID: currentAccount?.team?.id || "no-team",
            surface: EnvCtx.surface,
            version: EnvCtx.version,
            isInternal: isDropboxer || false,
            stackInCache: !!stack?.namespace_id,
          });
          navigate(`/stacks/${canonicalShareId}`, { replace: true });
          return true;
        }
      } catch (e) {
        logger.error("Failed to get Canonical SharingId", e);
      }

      return false;
    },
    [
      currentAccount?.team?.id,
      isDropboxer,
      navigate,
      shareId,
      stack?.namespace_id,
    ],
  );

  useDashTitle(stack?.stack_data?.name);

  useEffect(() => {
    // Switched to a different stack, so reset the last share id.
    if (
      lastRenderedShareId.current &&
      shareId !== lastRenderedShareId.current
    ) {
      lastRenderedShareId.current = "";
    }

    // If stack was rendered, but is now gone, then display not found.
    if (lastRenderedShareId.current && !stack) {
      setViewMode(ViewMode.NotFound);
      return;
    }

    setViewMode(ViewMode.Loading);
  }, [shareId, stack]);

  useEffect(() => {
    let canceled = false;

    async function previewLoop(): Promise<void> {
      logPageLoadMilestoneOnce(`previewLoop start`);

      // Use local cache only for the first call.
      let refresh = false;

      // Need to keep refreshing because the suggested stack items are added
      // one-by-one (non-batched). Cypress does not support batched adds (yet).
      const backoff = new Backoff(5000, 30_000);

      async function onError(hasRetriesLeft: boolean) {
        if (await tryRedirectToCanonicalShareUrl()) {
          return;
        }
        if (await tryRedirectToLoginPage()) {
          return;
        }
        setViewMode(ViewMode.NotFound);
        metrics.counter("preview_stack_error", 1, {
          teamID: currentAccount?.team?.id || "no-team",
          surface: EnvCtx.surface,
          version: EnvCtx.version,
          isInternal: isDropboxer || false,
          stackInCache: !!stack?.namespace_id,
          hasRetriesLeft,
        });
        await backoff.sleep();
      }

      // Try for a while, but don't spin forever.
      for (
        let attemptsLeft = MAX_PREVIEW_ATTEMPTS, attemptCount = 1;
        attemptsLeft > 0 && !canceled;
        attemptsLeft--, attemptCount++
      ) {
        try {
          const response = await previewStack(shareId, {
            refresh,
            fetchInBackground: true,
          });

          // Handling undefined in case the error is not thrown. Seen this in
          // Chrome but not sure about exact repro steps.
          if (!response) {
            await onError(attemptsLeft > 1);
            continue;
          }

          refresh = true;
          // Mark not loading as soon as we get a non-error response.
          // Otherwise we won't know when we have finished loading.
          setViewMode(ViewMode.Loaded);
          lastRenderedShareId.current = shareId;
          metrics.stats("preview_stack_success", attemptCount, {
            teamID: currentAccount?.team?.id || "no-team",
            surface: EnvCtx.surface,
            version: EnvCtx.version,
            isInternal: isDropboxer || false,
            stackInCache: !!stack?.namespace_id,
          });
          return;
        } catch {
          await onError(attemptsLeft > 1);
        }
      }
    }

    if (publicPreview) {
      if (stack?.namespace_id) {
        setViewMode(ViewMode.Loaded);
      }
    } else if (shareId !== "new") {
      previewLoop();
    }

    return () => {
      canceled = true;
    };
  }, [
    shareId,
    publicPreview,
    stack?.namespace_id,
    tryRedirectToLoginPage,
    tryRedirectToCanonicalShareUrl,
    currentAccount?.team?.id,
    isDropboxer,
  ]);

  const stackNameRef = useRef(stack?.stack_data?.name);
  stackNameRef.current = stack?.stack_data?.name;

  useEffect(() => {
    // Sends at most 3 times.
    // (1) with only shareId, (2) with stack name and (3) only if chat is enabled
    notifyInStackDetailPage(shareId, stackNameRef.current, isChatEnabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChatEnabled, shareId, stack?.stack_data?.name]);

  useEffect(() => {
    if (publicPreview) {
      return;
    }

    stack?.namespace_id; // added for dependency
    const stackName = stackNameRef.current;
    if (stackName) {
      auditLogUserOpenedStack(stackName, Date.now());
    }
  }, [publicPreview, stack?.namespace_id, stack?.stack_data?.creation_type]);

  const markStacksDataLoadComplete = useStackDetailsPagePerformanceTracking({
    showV2: true,
    showSharingModule,
    showSuggestions,
  });

  const { closeFlyoutPanel } = useFlyoutPanel();

  // Close dynamic panel when navigating away from stack page
  useEffect(() => {
    return () => {
      closeFlyoutPanel();
    };
    // We omit closeFlyoutPanel from the dependencies array to avoid
    // inadvertently closing the flyout panel when it is updated upon
    // re-rendering. We only wish to close it when this component is
    // unmounted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TODO support public previews in StackDetailPage
  if (publicPreview) {
    if (stack && items) {
      return <FullScreenPublicStackV2 />;
    }
    return <ProgressBar in isIndeterminate />;
  }

  if (viewMode === ViewMode.NotFound) {
    return <StackNotFound shareId={shareId} />;
  }

  if (!stack || !items) {
    return <ProgressBar in isIndeterminate />;
  }

  if (!currentAccount) {
    return <ProgressBar in isIndeterminate />;
  }

  markStacksDataLoadComplete();

  if (isFssStacksEnabled) {
    return (
      <StacksLoadingContainer>
        <StackDetailPageV3
          setShowSharingModule={setShowSharingModule}
          setShowSuggestions={setShowSuggestions}
        />
      </StacksLoadingContainer>
    );
  }

  return (
    <StacksLoadingContainer>
      <FullScreenStackV2
        currentAccount={currentAccount}
        setShowSharingModule={setShowSharingModule}
        setShowSuggestions={setShowSuggestions}
      />
    </StacksLoadingContainer>
  );
};

function useStackDetailsPagePerformanceTracking({
  showV2,
  showSuggestions,
  showSharingModule,
}: {
  showV2: boolean;
  showSuggestions: boolean;
  showSharingModule: boolean;
}) {
  const tags = useStacksDataCachedTags();

  const dependentModuleNames = useMemo(() => {
    return showV2
      ? [
          StackDetailsModule.STACKS_DATA,
          ...(showSharingModule ? [StackDetailsModule.SHARING] : []),
          ...(showSuggestions ? [StackDetailsModule.SUGGESTED_ITEMS] : []),
        ]
      : [
          StackDetailsModule.STACKS_DATA,
          ...(showSuggestions ? [StackDetailsModule.SUGGESTED_ITEMS] : []),
        ];
  }, [showSharingModule, showSuggestions, showV2]);

  // This aggregated metric is for all the modules combined.
  useAutoRecordAggregatedPageLatency(
    MetricPageName.STACK_DETAILS,
    dependentModuleNames,
    tags || getStacksDataCachedTags,
  );

  // This is for the stacks data module only. In the UI, the stacks data is
  // displayed in a few different cards, but since the data is the same,
  // we consider all of them to be one single module.
  const { markModuleLoadComplete } = useRecordModuleLatency(
    MetricPageName.STACK_DETAILS,
    StackDetailsModule.STACKS_DATA,
    tags || getStacksDataCachedTags,
  );

  return markModuleLoadComplete;
}

const StackNotFound: React.FC<{ shareId: string }> = ({ shareId }) => {
  const { reportPapEvent } = useMirageAnalyticsContext();

  useEffect(() => {
    const now = new Date();

    // Fields not defined in the PAP event itself.
    const extraProperties: Record<string, unknown> = {
      stackShareId: shareId,
      element_id: EnvCtx.surface + ".stack_page.stacks.shown_not_found",
      readable_time: now.toISOString(),
      startTimeMs: now.getTime(),
    };

    reportPapEvent(
      PAP_Select_DashUxa({ featureLine: "stacks", ...extraProperties }),
    );

    // This does not indicate an error, but sometimes it might be.
    logger.warn(`Stack not found: shareId=${shareId}`);
  }, [reportPapEvent, shareId]);

  return (
    <div className={styles.fullPage}>
      <div className={styles.iconContainer}>
        <LookMagnifyingGlassSpot />
      </div>
      <Title>{i18n.t("not_found")}</Title>
    </div>
  );
};
